import { Box, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import React from 'react';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { ObjectSchema } from 'yup';

import { CustomerItemDto } from '@/api/generated';
import { FormProviderRc } from '@/components/form/FormProviderRC.comp.tsx';
import { handleTableSubmitStopPropagation } from '@/components/form/util/handleTableSubmitStopPropagation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { ContactCreateEditFormContent } from '@/views/contactsCreate/components/form/components/ContactCreateEditFormContent.comp.tsx';
import { ContactsCreateSelectClient } from '@/views/contactsCreate/components/selectClient/ContactsCreateSelectClient.comp.tsx';
import { CreateContactFormModel } from '@/views/contactsCreate/model/CreateContactForm.model.ts';

interface Props {
  disabled: boolean;
  formMethods: UseFormReturn<CreateContactFormModel>;
  hideCustomerSelection: boolean;
  isEditing: boolean;
  onCancel?: () => void;
  onSubmit: SubmitHandler<CreateContactFormModel>;
  validation: ObjectSchema<CreateContactFormModel>;
}

export const ContactCreateEditForm: React.FC<Props> = props => {
  const {
    disabled,
    formMethods,
    hideCustomerSelection,
    isEditing,
    onCancel,
    onSubmit,
    validation,
  } = props;
  const navigate = useNavigate();
  const { t } = useTranslationWithPrefix('views.contactsCreate');

  const handleCancel = async () => {
    if (onCancel) {
      onCancel();
      return;
    }
    await navigate(-1);
  };

  const isDisabled = disabled;

  const handleCustomerSelected = (customer: CustomerItemDto | null) => {
    formMethods.setValue('contact.customerChanged', true);
    formMethods.setValue('contact.customer', customer);
  };

  const customer = formMethods.watch('contact.customer');

  return (
    <FormProviderRc {...formMethods} validation={validation}>
      <form onSubmit={handleTableSubmitStopPropagation(formMethods.handleSubmit(onSubmit))}>
        <Stack>
          <Typography>{isEditing ? t('title.update') : t('title.create')}</Typography>
          <Divider />
          <ContactCreateEditFormContent isDisabled={isDisabled} isEditing={isEditing} />
          <Divider flexItem />
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              px: 2,
            }}
          >
            {!hideCustomerSelection ? (
              <ContactsCreateSelectClient
                customer={customer}
                onCustomerSelected={handleCustomerSelected}
              />
            ) : (
              <div />
            )}
            <Box>
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: 'flex-end',
                }}
              >
                <Button onClick={handleCancel} variant="outlined">
                  {t('button.cancel')}
                </Button>
                <Button loading={isDisabled} type="submit">
                  {t('button.save')}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </form>
    </FormProviderRc>
  );
};
